"use client"

import { useState, useRef, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Play, Pause, RotateCcw, RotateCw, MoreHorizontal } from "lucide-react"

interface AudioPlayerProps {
  audioUrl?: string
  audioId?: string
}

export function AudioPlayer({ audioUrl, audioId }: AudioPlayerProps) {
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [speed, setSpeed] = useState(1.0)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio()
      
      audioRef.current.addEventListener('loadedmetadata', () => {
        setDuration(audioRef.current?.duration || 0)
      })
      
      audioRef.current.addEventListener('timeupdate', () => {
        setCurrentTime(audioRef.current?.currentTime || 0)
      })
      
      audioRef.current.addEventListener('ended', () => {
        setIsPlaying(false)
        setCurrentTime(0)
      })
    }

    if (audioUrl && audioRef.current.src !== audioUrl) {
      audioRef.current.src = audioUrl
      audioRef.current.load()
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current = null
      }
    }
  }, [audioUrl])

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = speed
    }
  }, [speed])

  const togglePlay = () => {
    if (!audioRef.current || !audioUrl) return

    if (isPlaying) {
      audioRef.current.pause()
    } else {
      audioRef.current.play()
    }
    setIsPlaying(!isPlaying)
  }

  const cycleSpeed = () => {
    const speeds = [0.5, 1.0, 1.5, 2.0]
    const currentIndex = speeds.indexOf(speed)
    setSpeed(speeds[(currentIndex + 1) % speeds.length])
  }

  const skipBackward = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.max(0, audioRef.current.currentTime - 10)
    }
  }

  const skipForward = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.min(duration, audioRef.current.currentTime + 10)
    }
  }

  // Generate waveform visualization
  const waveformBars = Array.from({ length: 150 }).map((_, i) => {
    const progress = currentTime / duration
    const isPast = i < progress * 150
    const height = Math.sin(i / 8) * 30 + Math.random() * 20 + 20
    return { height, isPast, index: i }
  })

  return (
    <div className="space-y-4">
      <div className="h-16 rounded-lg flex items-center">
        <div className="w-full h-12 flex items-center gap-[2px]">
          {waveformBars.map(({ height, isPast, index }) => (
            <div
              key={index}
              className={`flex-1 rounded-full transition-colors ${
                isPast ? "bg-foreground/70" : "bg-muted-foreground/20"
              }`}
              style={{
                height: `${height}%`,
                minWidth: "2px",
              }}
            />
          ))}
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Button
          onClick={togglePlay}
          size="icon"
          className="bg-foreground text-background hover:bg-foreground/90 rounded-full w-10 h-10"
          disabled={!audioUrl}
        >
          {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
        </Button>

        <Button
          onClick={cycleSpeed}
          variant="ghost"
          size="sm"
          className="text-foreground hover:bg-muted font-medium text-sm h-8 px-3"
        >
          {speed}x
        </Button>

        <Button
          variant="ghost"
          size="icon"
          className="text-foreground hover:bg-muted w-8 h-8"
          onClick={skipBackward}
        >
          <RotateCcw className="w-4 h-4" />
        </Button>

        <Button
          variant="ghost"
          size="icon"
          className="text-foreground hover:bg-muted w-8 h-8"
          onClick={skipForward}
        >
          <RotateCw className="w-4 h-4" />
        </Button>

        <div className="flex-1 text-center">
          <span className="text-foreground font-mono text-sm">
            {Math.floor(currentTime / 60)}:{(Math.floor(currentTime) % 60).toString().padStart(2, "0")} / {Math.floor(duration / 60)}:
            {(Math.floor(duration) % 60).toString().padStart(2, "0")}
          </span>
        </div>

        <Button variant="ghost" size="icon" className="text-foreground hover:bg-muted w-8 h-8">
          <MoreHorizontal className="w-4 h-4" />
        </Button>
      </div>
    </div>
  )
}

